import { Component, OnInit, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import Canvg from "canvg";
import { ApiService } from '../_services/api.service';
import { BehaviorSubject } from 'rxjs';

@Component({
    selector: 'true-export',
    templateUrl: './export.component.html',
    styleUrls: ['./export.component.scss'],
    standalone: false
})
export class ExportComponent implements OnInit {
  public date;
  public isReady = false;
  public rendering = false;

  private assessment;
  private questionnairesData;

  assessmentData$ = new BehaviorSubject<any>(null);

  // initialize PDF
  private doc = new jsPDF('p', 'mm', 'a4');
  private pageWidth = 210;
  private pageHeight = 295;
  private paddingX = 5;
  private paddingY = 5;

  @ViewChild('content', { 'static': true }) content:ElementRef;
  @ViewChild('analysis', { 'static': true }) analysis:ElementRef;

  constructor(private dialogRef: MatDialogRef<ExportComponent>, protected api: ApiService,) { }

  ngOnInit(): void {    
    this.initAndFormatDate();    
    this.api.getAssessment().subscribe((assessment) => {
      this.api.getAssessmentQuestionnaires().subscribe((data: any) => {        
        this.assessmentData$.next({ data, assessment });
        this.assessment = assessment;        
        this.questionnairesData = data;
      });
    });
  }

  initAndFormatDate() {
    const now = new Date();  
    const dateOptions: Intl.DateTimeFormatOptions = { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'numeric', 
      year: 'numeric' 
    };  
    const timeOptions: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    };  
    const formattedDate = new Intl.DateTimeFormat('en-GB', dateOptions).format(now);
    const formattedTime = new Intl.DateTimeFormat('en-GB', timeOptions).format(now);
    this.date = `${formattedDate}, ${formattedTime}`;
  }

  ngAfterViewInit() {
    setTimeout(() => {      
      const dialogElement = document.querySelector('.mat-mdc-dialog-container');      
      if (dialogElement) {
        dialogElement.classList.add('custom-dialog-style');
      }
    });
  }

  ready() {
    this.isReady = true;
  }

  getSectionData(sectionName: string): string[] {
    const section = this.questionnairesData.find(item => item.template.name === sectionName);
    if (!section) return [`No data found for ${sectionName}.`];
    const { questions, answers } = section.template;
    return questions.map((q, index) => {
        const answer = answers.find(a => a.questionId === q.id);
        const answerText = answer ? answer.answerText : "No answer provided.";
        return `${index + 1}. ${q.questionTitle}\n${answerText}\n`;
    });
  }

  getCanvasData(): string[] {
      return this.getSectionData("CANVAS");
  }

  getBriefData(): string[] {
      return this.getSectionData("BRIEF");
  }

  getOverallData(): string[] {
    // TODO: check if working
    const raw = this.assessment?.overall;
  
    if (!raw) {
      return ["No overall feedback available."];
    }
  
    let parsed;
  
    try {
      parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch (e) {
      console.error("Failed to parse overall feedback:", e);
      return ["Error: Could not parse overall feedback."];
    }
  
    const comments = parsed.comments;
    const recommendations = parsed.recommendations;
  
    const lines: string[] = [];
  
    if (Array.isArray(comments)) {
      lines.push("Comments:");
      comments.forEach((c, index) => {
        lines.push(`${index + 1}. ${c.title}:`);
        lines.push(`${c.text}\n`);
      });
    } else {
      lines.push("No valid comments found.");
    }
  
    if (Array.isArray(recommendations)) {
      lines.push("\nRecommendations:");
      recommendations.forEach((r, index) => {
        lines.push(`${index + 1}. ${r.title}:`);
        lines.push(`${r.text}\n`);
      });
    } else {
      lines.push("No valid recommendations found.");
    }
  
    return lines;
  }

  getTrlData(): string[] {
    const section = this.questionnairesData.find(item => item.template.name === "TRL");
    if (!section) return ["No TRL data found."];

    const { questions, answers } = section.template;

    // Sort answers by questionOrder (ascending) to ensure order
    const sortedQuestions = questions.sort((a, b) => a.questionOrder - b.questionOrder);
    const sortedAnswers = answers.sort((a, b) => {
        const qA = questions.find(q => q.id === a.questionId)?.questionOrder || 0;
        const qB = questions.find(q => q.id === b.questionId)?.questionOrder || 0;
        return qA - qB;
    });

    // Find the last NO answer
    let lastNoIndex = sortedAnswers.findIndex(a => a.answerText.trim().toLowerCase() === "no");
    
    if (lastNoIndex === -1) {
      if (sortedAnswers.findIndex(a => a.answerText.trim().toLowerCase() === "yes") === -1){
        return ["TRL evaluation not yet started."];
      }  else {
        // If there's no "NO" answer, assume all levels are reached
        lastNoIndex = sortedQuestions.length;
      }
    }

    // The TRL level reached is the last positive answer before the "NO"
    const reachedTrlIndex = lastNoIndex - 1;
    if (reachedTrlIndex < 0) return ["TRL level not yet started."];

    const reachedTrlQuestion = sortedQuestions[reachedTrlIndex];
    const reachedTrlLevel = reachedTrlIndex + 1; // Since TRL levels start from 1

    return [`The reached TRL level is **TRL ${reachedTrlLevel}**: ${reachedTrlQuestion.description.replace(/<\/?[^>]+(>|$)/g, "")}`];
  }  

  addSectionPage = function(title: string, content: string[], doc) {
    const sectionPaddingX = 15;  // Override padding for this function only
    const sectionPaddingY = 15;
    const maxWidth = this.pageWidth - 2 * sectionPaddingX; // Full width minus padding
    const lineSpacing = 6;

    doc.addPage();
    doc.setFontSize(16);
    doc.setTextColor(11, 53, 32); // Radiant Green (#0B3520)
    doc.setFont('Montserrat', 'bold');
    doc.text(title, sectionPaddingX, sectionPaddingY);

    doc.setFontSize(12);
    doc.setFont('Montserrat', 'medium');
    doc.setTextColor(112, 112, 112); // Dark Grey (#707070)
    let y = sectionPaddingY + 10;

    content.forEach((line, index) => {
        const wrappedText = doc.splitTextToSize(line, maxWidth);

        wrappedText.forEach((subLine) => {
            if (y > this.pageHeight - sectionPaddingY) {
                doc.addPage();
                y = sectionPaddingY;
            }
            doc.text(subLine, sectionPaddingX, y);
            y += lineSpacing;
        });

        y += 6; // Add extra space between questions
    });
  }

  capitalizeFirstLetter(text: string): string {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  addTitlePage(doc) {    
    const centerX = this.pageWidth / 2;
    const topMargin = 80;

    doc.setFont('Montserrat', 'bold');
    doc.setFontSize(28);
    doc.setTextColor(11, 53, 32); // Radiant Green (#0B3520)    
    //doc.text("Radiant's", centerX, topMargin - 10, { align: "center" });
    doc.text("Radiant MAG", centerX, topMargin, { align: "center" });

    doc.setFontSize(18);
    doc.setFont('Montserrat', 'medium');
    doc.setTextColor(112, 112, 112); // Dark Grey (#707070)
    
    const subtitle = `${this.capitalizeFirstLetter(this.assessment.name)} (${this.assessment.object.toLowerCase()})`;
    doc.text(subtitle, centerX, topMargin + 15, { align: "center" });

    doc.setFontSize(14);
    doc.setFont('Montserrat', 'regular');
    doc.setTextColor(222, 141, 53); // Radiant Orange (#DE8D35)
    doc.text("Self-evaluation report", centerX, topMargin + 30, { align: "center" });

    doc.setFontSize(12);
    doc.setFont('Montserrat', 'regular');
    doc.setTextColor(112, 112, 112); // Dark Grey (#707070)
    doc.text(this.date, centerX, topMargin + 45, { align: "center" });

  }

  async generatePDF(): Promise<Blob> {
    this.doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
      compress: true, // Enables internal compression
    });

    // Add Radiant-specific sections
    this.addTitlePage(this.doc);
    //this.addSectionPage("OVERALL COMMENTS AND RECCOMMENDATIONS", this.getOverallData(), this.doc);
    this.addSectionPage("BUSINESS MODEL CANVAS", this.getCanvasData(), this.doc);
    this.addSectionPage("BUSINESS BRIEF", this.getBriefData(), this.doc);
    this.addSectionPage("TECHNOLOGY READINESS LEVEL (TRL)", this.getTrlData(), this.doc);

    // Add true-node-chain SVGs
    await this.processSVGs();

    this.rendering = true;
    const div = document.getElementById('content');
    const options = {
        scale: 2,
        background: 'white',
        width: 1410,
        useCORS: true,
        onclone: function (clonedDoc) {
            let button = clonedDoc.getElementById('bottomUpButton');
            if (button) button.style.display = 'none';
            button = clonedDoc.getElementById('topDownButton');
            if (button) button.style.display = 'none';
        }
    };

    // Add a new page so that DEXiWare results start on their own page
    this.doc.addPage();

    return html2canvas(div, options).then((canvas) => {
        this.processCanvas(canvas);
        return this.doc.output("blob"); // Convert PDF to Blob
    });
  }

  async download() {
      const pdfBlob = await this.generatePDF();

      // Create a Blob URL and trigger download
      const link = document.createElement("a");
      link.href = URL.createObjectURL(pdfBlob);
      link.download = "export.pdf";
      link.click();
      URL.revokeObjectURL(link.href);

      this.dialogRef.close();
  }

  async sendForReview() {
    const pdfBlob = await this.generatePDF();
    // Create FormData
    const formData = new FormData();
    formData.append("file", pdfBlob, `report-${this.assessment.id}.pdf`);

    console.log("Sending report for review...");
    console.log(this.assessment);

    // Use API service to send the request
    this.api.sendForReview(this.assessment.id, formData)
      .subscribe({
          next: (data) => {
              console.log("Report sent:", data);
              alert("Report sent for review.");
          },
          error: (error) => {
              console.error("Error sending report:", error);
              alert("Failed to send report.");
          }
      });
  }

  async processSVGs() {
    // Process true-node-chain SVGs
    const chainDivElements = this.content.nativeElement.querySelectorAll('true-node-chain div');
    chainDivElements.forEach(item => {
        item.style.setProperty("position", "relative");
        item.style.setProperty("z-index", "1002");
    });

    const chainSvgElements = this.content.nativeElement.querySelectorAll('true-node-chain .bg-line');
    chainSvgElements.forEach(item => {
        item.style.setProperty('position', 'relative');
        item.style.setProperty('z-index', '1001');
    });

    const svgElements = this.content.nativeElement.querySelectorAll('svg-icon');
    await Promise.all([...svgElements].map(async (item) => {
        let svg = item.querySelector('svg');
        if (svg) {
            let xml = (new XMLSerializer()).serializeToString(svg);
            let canvas = document.createElement("canvas");
            const ctx = canvas.getContext('2d');
            ctx.canvas.width = item.getBoundingClientRect().width;
            ctx.canvas.height = item.getBoundingClientRect().height;

            let v = await Canvg.fromString(ctx, xml);
            v.start();

            let imgData = canvas.toDataURL("image/png");
            let img = document.createElement('img');
            img.src = imgData;

            item.parentNode.insertBefore(img, item);
            (item as HTMLElement).remove();
        }
    }));
  }

  processCanvas(canvas) {
    let position = 0;
    let canvasPageWidth = canvas.width;
    let canvasPageHeight = this.pageHeight * canvas.width / this.pageWidth;

    let subcanvas = document.createElement("canvas");
    let subcontext = subcanvas.getContext('2d');
    subcontext.canvas.width = canvasPageWidth;
    subcontext.canvas.height = canvasPageHeight;

    let headerHeight = this.analysis.nativeElement.getBoundingClientRect().top - this.content.nativeElement.getBoundingClientRect().top;
    if (headerHeight > canvasPageHeight) {
        let firstPageHeight = 1780;
        subcontext.drawImage(canvas, 0, 0, canvasPageWidth, firstPageHeight, 0, 0, canvasPageWidth, firstPageHeight);
        this.doc.addImage(subcanvas.toDataURL('image/png'), 'PNG', this.paddingX, this.paddingY, this.pageWidth - this.paddingX * 2, this.pageHeight - this.paddingY * 2);

        position = firstPageHeight;
        let leftoverHeight = headerHeight - firstPageHeight;
        this.doc.addPage();
        subcontext.clearRect(0, 0, canvasPageWidth, canvasPageHeight);
        subcontext.drawImage(canvas, 0, position, canvasPageWidth, leftoverHeight, 0, 0, canvasPageWidth, leftoverHeight);
        this.doc.addImage(subcanvas.toDataURL('image/png'), 'PNG', this.paddingX, this.paddingY, this.pageWidth - this.paddingX * 2, this.pageHeight - this.paddingY * 2);

        position += leftoverHeight;
    } else {
        subcontext.drawImage(canvas, 0, 0, canvasPageWidth, canvasPageHeight, 0, 0, canvasPageWidth, canvasPageHeight);
        this.doc.addImage(subcanvas.toDataURL('image/png'), 'PNG', this.paddingX, this.paddingY, this.pageWidth - this.paddingX * 2, this.pageHeight - this.paddingY * 2);

        position = canvasPageHeight;
    }

    while (position < canvas.height) {
        this.doc.addPage();
        subcontext.clearRect(0, 0, canvasPageWidth, canvasPageHeight);
        subcontext.drawImage(canvas, 0, position, canvasPageWidth, canvasPageHeight, 0, 0, canvasPageWidth, canvasPageHeight);
        this.doc.addImage(subcanvas.toDataURL('image/png'), 'PNG', this.paddingX, this.paddingY, this.pageWidth - this.paddingX * 2, this.pageHeight - this.paddingY * 2);
        position += canvasPageHeight - (this.paddingY * 0.5 * canvas.width / this.pageWidth);
    }
  }

}
